<template>
  <div class="container-fluid py-4">

    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-center mb-4 header-clinique">

      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-person-plus fs-2 text-primary"></i>

        <div>
          <h2 class="fw-bold mb-0">
            Création d'un Utilisateur
          </h2>

          <small class="text-muted">
            Les accès seront envoyés automatiquement par email
          </small>
        </div>
      </div>

      <router-link
        class="btn btn-primary btn-lg shadow-sm btn-clinique"
        :to="{ name: 'utilisateur' }"
      >
        <i class="bi bi-arrow-left-circle me-2"></i>
        Retour
      </router-link>
    </div>

    <!-- CARD -->
    <div class="card p-4 shadow-sm rounded card-clinique">

      <!-- ERREUR GENERALE (ex: email ou username déjà utilisé) -->
      <div v-if="globalError" class="alert alert-danger border-0 rounded-4 mb-4 d-flex align-items-start gap-2">
        <i class="bi bi-exclamation-triangle-fill fs-5"></i>
        <div>{{ globalError }}</div>
      </div>

      <!-- INFO -->
      <div class="alert alert-info border-0 rounded-4 mb-4">
        <div class="d-flex align-items-start gap-3">

          <i class="bi bi-envelope-check-fill fs-3 text-primary"></i>

          <div>
            <h6 class="fw-bold mb-1">
              Informations de connexion automatiques
            </h6>

            <p class="mb-0 small">
              Un lien d'activation (valable 72 heures) sera envoyé à l'adresse
              email de l'utilisateur pour qu'il choisisse lui-même son mot de passe.
            </p>
          </div>
        </div>
      </div>

      <!-- FORM -->
      <div class="row g-4">

        <!-- NOM -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">
            Nom
          </label>

          <input
            type="text"
            v-model="user.nom"
            class="form-control form-control-clinique"
            :class="{ 'is-invalid': error.nom }"
            placeholder="Nom"
            @input="error.nom = ''"
          >

          <div class="invalid-feedback">
            {{ error.nom }}
          </div>
        </div>

        <!-- PRENOM -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">
            Prénom
          </label>

          <input
            type="text"
            v-model="user.prenom"
            class="form-control form-control-clinique"
            :class="{ 'is-invalid': error.prenom }"
            placeholder="Prénom"
            @input="error.prenom = ''"
          >

          <div class="invalid-feedback">
            {{ error.prenom }}
          </div>
        </div>

        <!-- USERNAME -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">
            Nom utilisateur
          </label>

          <input
            type="text"
            v-model="user.username"
            class="form-control form-control-clinique"
            :class="{ 'is-invalid': error.username }"
            placeholder="Nom d'utilisateur"
            @input="error.username = ''; globalError = ''"
          >

          <div class="invalid-feedback">
            {{ error.username }}
          </div>
        </div>

        <!-- EMAIL -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">
            Email
          </label>

          <input
            type="email"
            v-model="user.email"
            class="form-control form-control-clinique"
            :class="{ 'is-invalid': error.email }"
            placeholder="Email professionnel"
            @input="error.email = ''; globalError = ''"
          >

          <div class="invalid-feedback">
            {{ error.email }}
          </div>
        </div>
      <div class="col-md-6">
  <label class="form-label fw-semibold">
    Téléphone
  </label>

  <input
    type="tel"
    v-model="user.telephone"
    class="form-control form-control-clinique"
    placeholder="Numéro de téléphone"
  >

  <small class="text-muted">
    Ajoutez l'indicatif (+xxx) si le numéro n'est pas local à la clinique.
  </small>
</div>

        <!-- ROLE -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">
            Rôle
          </label>

          <select
            v-model="user.typeUser"
            class="form-select form-select-clinique"
            :class="{ 'is-invalid': error.type }"
          >
            <option value="" disabled>
              -- Sélectionnez un rôle --
            </option>

            <option
              v-for="type in typesUser.filter(t => t.nom !== 'Super Admin')"
              :key="type.id"
              :value="type.id"
            >
              {{ type.nom }}
            </option>
          </select>

          <div class="invalid-feedback">
            {{ error.type }}
          </div>
        </div>

        <!-- STATUS -->
        <div class="col-md-6 d-flex align-items-center mt-3">

          <div class="form-check form-switch">

            <input
              class="form-check-input"
              type="checkbox"
              id="enabled"
              v-model="user.enabled"
            >

            <label
              class="form-check-label ms-2 fw-semibold"
              for="enabled"
            >
              Utilisateur activé
            </label>
          </div>
        </div>
      </div>

      <!-- BUTTONS -->
      <div class="d-flex justify-content-end gap-3 mt-4">

        <button
          class="btn btn-light border shadow-sm px-4 py-2"
          @click="cancel"
        >
          <i class="bi bi-x-circle me-1"></i>
          Annuler
        </button>

        <button
          class="btn btn-success shadow px-4 py-2 btn-clinique"
          :disabled="isLoading"
          @click="saveUser"
        >

          <span
            v-if="isLoading"
            class="spinner-border spinner-border-sm me-2"
          ></span>


          <i
            v-else
            class="bi bi-save me-1"
          ></i>

          <span v-if="isLoading">
            Création...
          </span>

          <span v-else>
            Créer l'utilisateur
          </span>

        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {

  data() {

    return {

      isLoading: false,

      user: {
        nom: '',
        prenom: '',
        username: '',
        telephone: '',
        email: '',
        enabled: true,
        typeUser: ''
      },

      error: {
        nom: '',
        prenom: '',
        username: '',
        email: '',
        type: ''
      },

      globalError: '',

      typesUser: []
    }
  },

  methods: {

    /**
     * Récupération rôles
     */
    async getTypeUser() {

      try {

        const response = await api.getItems('type_users')

        this.typesUser = response.data.data

      } catch (error) {

        console.error(
          "Erreur récupération types utilisateurs",
          error
        )
      }
    },

    /**
     * Validation formulaire
     */
    validateForm() {

      this.error = {

        nom: this.user.nom
          ? ''
          : 'Le nom est requis',

        prenom: this.user.prenom
          ? ''
          : 'Le prénom est requis',

        username: this.user.username
          ? ''
          : 'Le nom utilisateur est requis',

        email: this.user.email
          ? ''
          : 'L’email est requis',

        type: this.user.typeUser
          ? ''
          : 'Le rôle est requis'
      }

      return !Object
        .values(this.error)
        .some(e => e !== '')
    },

    /**
     * Création utilisateur
     */
   async saveUser() {

  if (!this.validateForm()) {
    return
  }

  if (this.isLoading) {
    return
  }

  this.isLoading = true
  this.globalError = ''

  try {

    const payload = {

      nom: this.user.nom,
      prenom: this.user.prenom,
      username: this.user.username,
      telephone: this.user.telephone,
      email: this.user.email,
      enabled: this.user.enabled,

      typeUser:this.user.typeUser
    }

    await api.createItem(
      'users',
      payload
    )

   // alert(   "Utilisateur créé avec succès. Les accès ont été envoyés par email." )

    this.$router.push({
      name: 'utilisateur'
    })

  } catch (error) {

    console.error(error)

    // Gère les deux formats possibles : notre JsonResponse ({message: ...})
    // et le format standard API Platform pour une exception classique
    // ({detail: ...}) — utile tant que le backend déployé n'est pas
    // forcément encore à jour partout.
    const message = error.response?.data?.message || error.response?.data?.detail

     if (error.response?.status === 409 || error.response?.status === 400) {

      // Seul le doublon d'email est vérifié côté backend désormais
      this.error.email = message

      this.globalError = message

    } else {

      this.globalError = message || "Erreur lors de la création utilisateur."
    }

  } finally {

    this.isLoading = false
  }
},

    /**
     * Annuler
     */
    cancel() {

      this.$router.push({
        name: 'utilisateur'
      })
    }
  },

  mounted() {

    this.getTypeUser()
  }
}
</script>

<style scoped>

/* HEADER */
.header-clinique {
  padding: 18px 22px;
  background: #eef6ff;
  border-radius: 16px;
}

/* CARD */
.card-clinique {
  border: none;
  border-radius: 18px;
  background: #ffffff;
}

/* INPUTS */
.form-control-clinique,
.form-select-clinique {

  border-radius: 12px;
  border: 1px solid #dbe7f2;
  padding: 12px 15px;

  transition: all 0.25s ease;
}

.form-control-clinique:focus,
.form-select-clinique:focus {

  border-color: #0d6efd;

  box-shadow:
    0 0 0 0.2rem rgba(13,110,253,.12);
}

/* BUTTON */
.btn-clinique {

  background:
    linear-gradient(
      90deg,
      #0d6efd,
      #6610f2
    );

  border: none;

  border-radius: 12px;

  transition: all 0.25s ease;
}

.btn-clinique:hover {

  transform: translateY(-2px);

  box-shadow:
    0 10px 20px rgba(0,0,0,.15);
}

/* ALERT */
.alert-info {

  background: #eef7ff;
}

</style>